import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Button, 
  Input, 
  Select,
  Table, 
  Space, 
  Tag, 
  Popconfirm, 
  message,
  Tooltip,
  Card,
  Typography,
  Divider,
  Form
} from 'antd';
import { 
  ShareAltOutlined, 
  DeleteOutlined, 
  UserOutlined,
  EyeOutlined,
  EditOutlined
} from '@ant-design/icons';
import { sharesAPI, usersAPI } from '../../services/api';

const { Text } = Typography;
const { Option } = Select;

const ShareManager = ({ contact, visible, onClose }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('write');
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && contact) {
      fetchShares();
    }
  }, [visible, contact]);

  const fetchShares = async () => {
    try {
      setLoading(true);
      const response = await sharesAPI.getContactShares(contact.id);
      if (response.data.success) {
        setShares(response.data.data);
      }
    } catch (error) {
      message.error('Erreur lors du chargement des partages');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      const values = await form.validateFields();
      setShareLoading(true);
      
      const response = await sharesAPI.create({
        contact_id: contact.id,
        email: values.email.trim(),
        permission
      });

      if (response.data.success) {
        message.success('Contact partagé avec succès');
        form.resetFields();
        setEmail('');
        setPermission('write');
        fetchShares();
      }
    } catch (error) {
      if (error.errorFields) {
        // Erreur de validation du formulaire
        return;
      }
      
      // Gérer les différentes erreurs HTTP
      if (error.response?.status === 400) {
        message.error(error.response?.data?.message || 'Erreur de validation');
      } else if (error.response?.status === 404) {
        message.error('Ce destinataire n\'existe pas dans le système');
      } else {
        message.error(error.response?.data?.message || 'Erreur lors du partage');
      }
    } finally {
      setShareLoading(false);
    }
  };

  const handleDeleteShare = async (shareId) => {
    try {
      await sharesAPI.delete(shareId);
      message.success('Partage supprimé avec succès');
      fetchShares();
    } catch (error) {
      message.error('Erreur lors de la suppression du partage');
    }
  };

  const columns = [
    {
      title: 'Utilisateur',
      dataIndex: ['recipient', 'first_name'],
      key: 'recipient',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <span>
            {record.recipient.first_name && record.recipient.last_name 
              ? `${record.recipient.first_name} ${record.recipient.last_name}`
              : record.recipient.email
            }
          </span>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: ['recipient', 'email'],
      key: 'email',
      render: (email) => <Text type="secondary">{email}</Text>
    },
    {
      title: 'Permission',
      dataIndex: 'permission',
      key: 'permission',
      render: (permission) => (
        <Tag color={permission === 'write' ? 'green' : 'blue'}>
          {permission === 'write' ? 'Écriture' : 'Lecture'}
          {permission === 'write' ? <EditOutlined style={{ marginLeft: 4 }} /> : <EyeOutlined style={{ marginLeft: 4 }} />}
        </Tag>
      )
    },
    {
      title: 'Partagé par',
      dataIndex: ['sharer', 'first_name'],
      key: 'sharer',
      render: (text, record) => (
        <Text type="secondary">
          {`${record.sharer.first_name} ${record.sharer.last_name}`}
        </Text>
      )
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString('fr-FR')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Êtes-vous sûr de vouloir supprimer ce partage ?"
          onConfirm={() => handleDeleteShare(record.id)}
          okText="Oui"
          cancelText="Non"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
      )
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <ShareAltOutlined />
          <span>Gérer les partages - {contact?.first_name} {contact?.last_name}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Text strong>Nouveau partage</Text>
          <Input.Group compact>
            <Form.Item
              name="email"
              noStyle
              rules={[
                { required: true, message: 'Veuillez entrer une adresse email' },
                { type: 'email', message: 'Veuillez entrer une adresse email valide' }
              ]}
            >
              <Input
                placeholder="Email du destinataire"
                prefix={<UserOutlined />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: '50%' }}
              />
            </Form.Item>
            <Form.Item
              name="permission"
              noStyle
              initialValue="write"
            >
              <Select
                value={permission}
                onChange={setPermission}
                style={{ width: '25%' }}
              >
                <Option value="read">Lecture</Option>
                <Option value="write">Écriture</Option>
              </Select>
            </Form.Item>
            <Button 
              type="primary" 
              icon={<ShareAltOutlined />}
              onClick={handleShare}
              loading={shareLoading}
              style={{ width: '25%' }}
            >
              Partager
            </Button>
          </Input.Group>
        </Form>
      </Card>

      <Divider />

      <Table
        columns={columns}
        dataSource={shares}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        locale={{
          emptyText: 'Aucun partage pour ce contact'
        }}
      />
    </Modal>
  );
};

export default ShareManager;
